
import { Component, Prop, Vue } from 'vue-property-decorator';
import ElementConfiguration from './configuration/ElementConfiguration';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import ElementBase from './elements/ElementBase';
import ActivityStatusSum from './elements/ActivityStatusSum';
import Column from './elements/Column';
import Organization from './elements/Organization';
import Row from './elements/Row';
import ActivityAssigmentList from './elements/ActivityAssigmentList';
import DateTime from './elements/DateTime';
import Container from './elements/Container';
import DashboardConfiguration from './configuration/DashboardConfiguration';
import { initClient } from '../../api';
import { Dashboard as DashboardModel, DashboardApiUiClient } from '../backend/backendapi';
import TitleBar from "./elements/TitleBar/TitleBar";
import ActivityStatusSumTable from "./elements/ActivityStatusSumTable/ActivityStatusSumTable";
import AuditHistoryChart from "./elements/AuditHistoryChart/AuditHistoryChart";
import AccountSelector from "./elements/AccountSelector";
import { evaluate } from "./expression";
import { getComponentName } from './elements/components';

@Component({
  name: 'Dashboard',
  components: {
    AccountSelector,
    ActivityAssigmentList,
    ActivityStatusSum,
    ActivityStatusSumTable,
    AuditHistoryChart,
    Column,
    Container,
    DateTime,
    Organization,
    Row,
    TitleBar,
  },
})
export default class Dashboard extends Vue {
  private dashboardApi = initClient(DashboardApiUiClient);
  private signalR?: HubConnection;

  @Prop({ required: true })
  readonly accountId!: number;
  @Prop({ required: true })
  readonly name!: string;

  dashboardLoaded = false;
  dashboardLoadingFailed = false;
  private dashboard: DashboardModel | null = null;
  
  getComponentName = getComponentName;

  private get json(): string {
    return this.dashboard?.json ?? '{}';
  }

  get dashboardConfiguration(): DashboardConfiguration {
    const dashboard = JSON.parse(this.json) as DashboardConfiguration;
    dashboard.theme ??= 'light';
    dashboard.rootElement ??= {} as any;
    return dashboard;
  }

  get rootConfiguration(): ElementConfiguration {
    const root = this.dashboardConfiguration.rootElement;
    root.type ??= 'div' as any;
    return root;
  }

  get theme(): string {
    return evaluate(this.dashboardConfiguration.theme, {});
  }

  async mounted(): Promise<void> {
    await this.loadDashboard();
    this.signalR = this.initSignalR();
  }

  private async loadDashboard(): Promise<void> {
    this.dashboardLoadingFailed = false;
    try {
      this.dashboard = await this.dashboardApi.getByAccountIdAndName(this.accountId, this.name);
      document.title = [this.dashboard?.title, 'Dashboard'].filter(e => e).join(' - ');
    } catch (e) {
      this.dashboardLoadingFailed = true;
    } finally {
      this.dashboardLoaded = true;
    }
  }

  beforeDestroy(): void {
    this.signalR?.stop();
  }

  private initSignalR(): HubConnection {
    const connection = new HubConnectionBuilder().withUrl('dashboardHub').build();
    connection.on('DashboardDataUpdate', this.handleDashboardDataUpdate.bind(this));
    connection.onclose(() => this.reconnectSignalR(connection)); // Restart connection after 5 seconds.
    this.reconnectSignalR(connection, 0);
    return connection;
  }

  private reconnectSignalR(connection, delay = 5000): void {
    setTimeout(() => {
      connection.start()
          .then(_ => connection.invoke('SubscribeDashboard', this.dashboard?.id ?? ''))
          .catch(err => {
            console.error(err);
            this.reconnectSignalR(connection);
          });
    }, delay);
  }

  private handleDashboardDataUpdate(elementPath: string, json: string): void {
    (this.$refs.element as ElementBase).onInformationPush(elementPath, json);
  }
}
